// Venda da mesa no caixa, uma por forma de pagamento (dono, 22/10/2026).
//
// Antes (Mesas.js) o lançamento falhava e a tela SEGUIA: toast vermelho,
// pagamento gravado na mesa e pedidos concluídos — o dinheiro entrava na mesa e
// em caixa nenhum (#58524, #43887 e os casos sem caixa aberto). Agora: sem caixa
// aberto nada é lançado; se uma forma falhar depois de outra já ter entrado, a
// que entrou é estornada e a mesa não grava nada. O chamador só grava o
// pagamento quando `ok` é true. O servidor recusa do lado dele também
// (api/v1/caixa/trava_mesa.py) — isto aqui é a mensagem clara para o atendente.
const API = process.env.REACT_APP_BACKEND_URL || "";

export const SEM_CAIXA = "Abra o caixa para receber: não há caixa aberto para lançar o pagamento da mesa.";
const TENTATIVAS = 2;

const cabecalhos = (token) => ({ "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) });
const reais = (v) => `R$ ${(Number(v) || 0).toFixed(2).replace(".", ",")}`;

/** Id do caixa aberto, ou null. Erro de rede estoura para o chamador. */
export async function caixaAbertoAgora({ token, fetchFn = fetch, api = API } = {}) {
  const r = await fetchFn(`${api}/api/caixa/status`, { headers: cabecalhos(token) });
  const data = await r.json().catch(() => null);
  return data?.id && data.status !== "fechado" ? data.id : null;
}

async function detalheDe(resposta) {
  try {
    const corpo = await resposta.json();
    return typeof corpo?.detail === "string" ? corpo.detail : "";
  } catch (_) {
    return "";
  }
}

async function registrar({ api, caixaId, body, token, fetchFn }) {
  let motivo = "";
  for (let tentativa = 1; tentativa <= TENTATIVAS; tentativa++) {
    try {
      const r = await fetchFn(`${api}/api/caixa/${caixaId}/registrar-venda`, { method: "POST", headers: cabecalhos(token), body });
      if (r.ok) {
        const venda = await r.json().catch(() => null);
        return { ok: true, venda: venda?.id ? { caixa_id: caixaId, venda_id: venda.id } : undefined };
      }
      motivo = (await detalheDe(r)) || `HTTP ${r.status}`;
      // O servidor recusou de propósito (caixa fechado, valor inválido): repetir não muda
      if (r.status >= 400 && r.status < 500) break;
    } catch (e) {
      motivo = e?.message || "falha de rede";
    }
  }
  return { ok: false, motivo };
}

async function estornar({ api, token, fetchFn, vendas }) {
  const naoEstornadas = [];
  for (const v of vendas) {
    try {
      const r = await fetchFn(`${api}/api/caixa/${v.caixa.caixa_id}/movimentos/${v.caixa.venda_id}/estornar`, { method: "POST", headers: cabecalhos(token) });
      if (!r.ok) naoEstornadas.push(v);
    } catch (_) {
      naoEstornadas.push(v);
    }
  }
  return naoEstornadas;
}

/**
 * Lança no caixa aberto cada forma com valor > 0.
 * Devolve `{ ok: true, vendas }` (`vendas[n]` = `{caixa_id, venda_id}` da forma n,
 * para a lixeira estornar) ou `{ ok: false, erro }` com a mensagem para o atendente.
 */
export async function lancarVendasNoCaixa({ pagamentos, pedidoId, pedidoCodigo, token, fetchFn = fetch, api = API }) {
  let caixaId;
  try {
    caixaId = await caixaAbertoAgora({ token, fetchFn, api });
  } catch (e) {
    return { ok: false, erro: `Não consegui consultar o caixa (${e?.message || "falha de rede"}). Nada foi gravado na mesa: tente de novo.` };
  }
  if (!caixaId) return { ok: false, erro: SEM_CAIXA };

  const vendas = [];
  const lancadas = [];
  for (const [n, p] of (pagamentos || []).entries()) {
    const valor = Number(p.value) || 0;
    // O servidor recusa valor zero ou negativo (v1.5.789) — não é venda.
    if (!(valor > 0)) continue;
    const body = JSON.stringify({
      valor, forma_pagamento: p.code,
      // pedido_id liga a venda ao pedido: é por ele que o cancelamento estorna
      pedido_id: pedidoId || undefined, pedido_codigo: pedidoCodigo, modulo: "mesas",
    });
    const r = await registrar({ api, caixaId, body, token, fetchFn });
    if (r.ok) {
      vendas[n] = r.venda;
      if (r.venda) lancadas.push({ ...p, caixa: r.venda });
      continue;
    }
    // Uma forma falhou: o que já entrou sai, e a mesa não grava nada
    const naoEstornadas = await estornar({ api, token, fetchFn, vendas: lancadas });
    let erro = `Venda de ${reais(valor)} (${p.label || p.code}) não entrou no caixa (${r.motivo}). Nada foi gravado na mesa: confira o caixa e confirme de novo.`;
    if (naoEstornadas.length) {
      const resto = naoEstornadas.map((v) => `${reais(v.value)} (${v.label || v.code})`).join(", ");
      erro += ` Atenção: ${resto} já tinha entrado no caixa e não consegui estornar — estorne em Financeiro → Caixa → Movimentos antes de cobrar de novo.`;
    }
    return { ok: false, erro, estornadas: lancadas.length - naoEstornadas.length, naoEstornadas };
  }
  return { ok: true, vendas };
}
